import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon.jsx";
import { useTheme } from "@/context/theme.js";
import { useAuth } from "@/context/auth.js";

/*
 * Ported from html-markup/assets/js/shell.js topbarHtml().
 *
 * The breadcrumb nav and the actions area are empty slots — pages fill them by
 * rendering <PageChrome>, which portals into the nodes handed up through
 * `onSlots`. See layouts/chrome.js for why portals rather than state.
 */
export default function Topbar({ onToggleSidebar, onSlots, user }) {
    const { theme, toggleTheme } = useTheme();
    const crumbsRef = useRef(null);
    const actionsRef = useRef(null);

    // Publish both slot nodes once they exist, so PageChrome can portal into them.
    useEffect(() => {
        onSlots({ crumbs: crumbsRef.current, actions: actionsRef.current });
    }, [onSlots]);

    return (
        <header className="sticky top-0 z-20 flex h-15 items-center gap-3 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/85">
            <button
                type="button"
                onClick={onToggleSidebar}
                aria-label="Toggle sidebar"
                className="grid size-9 shrink-0 place-items-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
                <Icon name="menu" className="size-5" />
            </button>

            <nav
                ref={crumbsRef}
                aria-label="Breadcrumb"
                className="hidden items-center gap-1.5 text-[13px] text-slate-400 sm:flex dark:text-slate-500"
            />

            <div className="flex-1" />

            <div ref={actionsRef} className="flex items-center gap-2.5" />

            <button
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
                className="grid size-9 shrink-0 place-items-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
                <Icon name={theme === "dark" ? "sun" : "moon"} className="size-5" />
            </button>

            <div className="mx-1 h-6 w-px bg-slate-200 dark:bg-slate-700" />

            <UserMenu user={user} />
        </header>
    );
}

function UserMenu({ user }) {
    const { logout } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Close on an outside click or Escape.
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape") setOpen(false);
        };

        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const name = user?.name || "—";
    const initials =
        name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase())
            .join("") || "?";
    const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "";

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="menu"
                className="flex items-center gap-2.5 rounded-full py-1 pr-2.5 pl-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
            >
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                    {initials}
                </span>
                <span className="hidden text-left leading-tight sm:block">
                    <span className="block text-[13px] font-semibold">{name}</span>
                    <span className="block text-[11px] text-slate-400 dark:text-slate-500">
                        {[roleLabel, user?.organizationName].filter(Boolean).join(" · ")}
                    </span>
                </span>
                <Icon name="chevronDown" className="size-3.5 text-slate-400" />
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute top-[calc(100%+8px)] right-0 z-50 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                >
                    <div className="border-b border-slate-100 px-2.5 py-2 dark:border-slate-800">
                        <p className="text-[13px] font-semibold">{name}</p>
                        <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">
                            {user?.email || "—"}
                        </p>
                    </div>

                    <MenuLink to="/profile" icon="user" onClick={() => setOpen(false)}>
                        Profile
                    </MenuLink>
                    <MenuLink to="/organization" icon="cog" onClick={() => setOpen(false)}>
                        Organization settings
                    </MenuLink>

                    <div className="my-1.5 h-px bg-slate-100 dark:bg-slate-800" />

                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            logout();
                        }}
                        role="menuitem"
                        className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                    >
                        <Icon name="logout" className="size-4" />
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

function MenuLink({ to, icon, children, onClick }) {
    return (
        <Link
            to={to}
            onClick={onClick}
            role="menuitem"
            className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
        >
            <Icon name={icon} className="size-4" />
            {children}
        </Link>
    );
}
